import _ from 'lodash';
import { v4 } from 'uuid';

import { simpleCheck } from './LendingAPIProviderHelper';

class LendingAPIProvider {
    deserialiseState(state) {
        let deserialised = {};

        if (state.application) {
            deserialised.application = this.deserialiseApplication(
                state.application
            );
        }

        simpleCheck(deserialised, state, 'partner');
        simpleCheck(deserialised, state, 'state_key');

        if (state.ui) {
            deserialised.ui = this.deserialiseUI(state);
        }

        return deserialised;
    }

    serialiseState(state) {
        let serialised = {};

        if (state.application) {
            serialised.application = this.serialiseApplication(
                state.application
            );
        }

        simpleCheck(serialised, state, 'ui');
        simpleCheck(serialised, state, 'partner');
        simpleCheck(serialised, state, 'state_key');

        return serialised;
    }

    deserialiseApplication(application) {
        let deserialised = {};

        if (application.people) {
            deserialised.people = this.deserialisePeople(application.people);
        }

        if (application.requested_products) {
            deserialised.loan_request = this.deserialiseLoanRequest(
                application.requested_products
            );
        }

        if (application.company) {
            deserialised.company = this.deserialiseCompany(application.company);
        }

        return deserialised;
    }

    serialiseApplication(application) {
        let serialised = {};

        if (application.people) {
            serialised.people = this.serialisePeople(application.people);
        }

        if (application.company) {
            serialised.company = this.serialiseCompany(application.company);
        }

        return serialised;
    }

    deserialiseUI(state) {
        let deserialised = state.ui;

        if (
            _.isUndefined(state.ui.manualCompanyName) &&
            (_.get(state, 'application.company.registered_company_name') ||
                _.get(state, 'application.company.company_number'))
        ) {
            deserialised.manualCompanyName = true;
        }

        return deserialised;
    }

    deserialiseCompany(company) {
        let deserialised = {};

        simpleCheck(deserialised, company, 'online_sales_percentage');
        simpleCheck(deserialised, company, 'company_number');
        simpleCheck(deserialised, company, 'number_of_employees');
        simpleCheck(deserialised, company, 'bank_details');
        simpleCheck(deserialised, company, 'industry');

        if (company.type) {
            deserialised.type = this.deserialiseCompanyType(company.type);
        }

        if (company.website) {
            deserialised.website = this.deserialiseWebsite(company.website);
        }

        if (company.vat_status) {
            this.deserialiseVATRegisterStatus(deserialised, company.vat_status);
            this.deserialiseVATRegisterNumber(deserialised, company.vat_status);
        }

        if (company.trading_from_date) {
            deserialised.registered = company.trading_from_date.substr(0, 7);
        }

        if (company.registered_company_name) {
            deserialised.company_name = company.registered_company_name;
        }

        if (company.trading_name) {
            deserialised.trading_as = company.trading_name;
        }

        if (company.last_12_months_turnover) {
            // parseInt to get rid of any decimals where the form fields do not support decimals
            deserialised.annual_turnover = parseInt(
                company.last_12_months_turnover.amount
            );
            deserialised.annual_turnover_datetime =
                company.last_12_months_turnover.datetime;
        }

        if (company.monthly_turnover) {
            // parseInt to get rid of any decimals where the form fields do not support decimals
            deserialised.monthly_turnover = parseInt(
                company.monthly_turnover.amount
            );
            deserialised.monthly_turnover_datetime =
                company.monthly_turnover.datetime;
        }

        if (company.last_12_months_profit) {
            // parseInt to get rid of any decimals where the form fields do not support decimals
            deserialised.annual_business_profit = parseInt(
                company.last_12_months_profit.amount
            );
            deserialised.annual_business_profit_datetime =
                company.last_12_months_profit.datetime;
        }

        if (_.isArray(company.third_party_providers)) {
            deserialised.providers = this.deserialiseProviders(
                company.third_party_providers
            );
        }

        return deserialised;
    }

    deserialiseVATRegisterStatus(deserialised, vat_status) {
        deserialised.vat_registered_status = !!vat_status.is_vat_registered;
    }

    deserialiseVATRegisterNumber(deserialised, vat_status) {
        if (vat_status.vat_number) {
            deserialised.vat_registered_number = vat_status.vat_number;
        }
    }

    serialiseCompany(company) {
        let serialised = {};

        simpleCheck(serialised, company, 'online_sales_percentage');
        simpleCheck(serialised, company, 'company_number');
        simpleCheck(serialised, company, 'number_of_employees');
        simpleCheck(serialised, company, 'bank_details');
        simpleCheck(serialised, company, 'industry');

        if (company.type) {
            serialised.type = this.serialiseCompanyType(company.type);
        }

        if (company.website) {
            serialised.website = this.serialiseWebsite(company.website);
        }

        if (_.isBoolean(company.vat_registered_status)) {
            serialised.vat_status = this.serialiseVATRegisteredStatus(company);
        }

        if (company.registered) {
            serialised.trading_from_date = company.registered + '-01';
        }

        if (company.company_name) {
            serialised.registered_company_name = company.company_name;
        }

        if (company.trading_as) {
            serialised.trading_name = company.trading_as;
        }

        if (_.isNumber(company.annual_turnover)) {
            serialised.last_12_months_turnover =
                this.serialiseTurnover(company);
        }

        if (_.isNumber(company.monthly_turnover)) {
            serialised.monthly_turnover =
                this.serialiseMonthlyTurnover(company);
        }

        if (_.isNumber(company.annual_business_profit)) {
            serialised.last_12_months_profit = this.serialiseProfit(company);
        }

        if (_.isArray(company.providers)) {
            serialised.third_party_providers = this.serialiseProviders(
                company.providers
            );
        }

        return serialised;
    }

    serialiseTurnover(company) {
        return {
            amount: company.annual_turnover,
            datetime: company.annual_turnover_datetime
        };
    }

    serialiseMonthlyTurnover(company) {
        return {
            amount: company.monthly_turnover,
            datetime: company.monthly_turnover_datetime
        };
    }

    serialiseProfit(company) {
        return {
            amount: company.annual_business_profit,
            datetime: company.annual_business_profit_datetime
        };
    }

    deserialiseWebsite(website) {
        return website.replace('https://', '').replace('http://', '');
    }

    serialiseWebsite(website) {
        if (
            website.indexOf('https://') !== -1 ||
            website.indexOf('http://') !== -1
        ) {
            return website;
        }

        return `https:\/\/${website}`;
    }

    deserialiseProviders(third_party_providers) {
        return third_party_providers;
    }

    serialiseProviders(providers) {
        return providers;
    }

    serialiseVATRegisteredStatus(company) {
        let serialised = {
            is_vat_registered: company.vat_registered_status
        };

        if (_.isString(company.vat_registered_number)) {
            serialised.vat_number = company.vat_registered_number;
        }

        return serialised;
    }

    deserialiseCompanyType(companyType) {
        const deserialiseCompanyTypes = {
            limited_liability_company: 'ltd',
            limited_liability_partnership: 'llp',
            sole_trader: 'st',
            ordinary_partnership: 'op',
            public_limited_company: 'plc'
        };

        return deserialiseCompanyTypes[companyType] || companyType;
    }

    serialiseCompanyType(companyType) {
        const serialiseCompanyTypes = {
            ltd: 'limited_liability_company',
            llp: 'limited_liability_partnership',
            st: 'sole_trader',
            op: 'ordinary_partnership',
            other: 'other',
            plc: 'public_limited_company'
        };

        return serialiseCompanyTypes[companyType] || companyType;
    }

    deserialiseDuration(duration) {
        return duration + 'm';
    }

    serialiseDuration(term) {
        return _.parseInt(term.replace('m', ''));
    }

    getProductType(requested_products) {
        return Object.keys(requested_products)[0];
    }

    deserialiseLoanRequest(requested_products) {
        const productType = this.getProductType(requested_products);
        let deserialised = {};

        deserialised.product_type = productType;

        if (requested_products[productType].approval.amount) {
            deserialised.amount =
                requested_products[productType].approval.amount;
        }

        if (requested_products[productType].approval.urgency) {
            deserialised.urgency =
                requested_products[productType].approval.urgency;
        }

        if (requested_products[productType].approval.purpose) {
            deserialised.purpose =
                requested_products[productType].approval.purpose;
        }

        if (requested_products[productType].approval.detailed_purpose) {
            deserialised.detailed_reason =
                requested_products[productType].approval.detailed_purpose;
        }

        if (requested_products[productType].approval.duration) {
            deserialised.term = this.deserialiseDuration(
                requested_products[productType].approval.duration
            );
            deserialised.duration =
                requested_products[productType].approval.duration;
        }

        return deserialised;
    }

    serialiseRequestedProducts(loan_request) {
        let productType = loan_request.product_type;
        if (productType === undefined) {
            productType = 'credit_facility';
        }
        let serialised = {};

        if (loan_request.amount) {
            serialised[productType] = _.merge(serialised[productType], {
                approval: { amount: loan_request.amount }
            });
        }

        if (loan_request.urgency) {
            serialised[productType] = _.merge(serialised[productType], {
                approval: { urgency: loan_request.urgency }
            });
        }

        if (loan_request.purpose) {
            serialised[productType] = _.merge(serialised[productType], {
                approval: { purpose: loan_request.purpose }
            });
        }

        if (loan_request.detailed_reason) {
            serialised[productType] = _.merge(serialised[productType], {
                approval: { detailed_purpose: loan_request.detailed_reason }
            });
        }

        if (loan_request.term) {
            serialised[productType] = _.merge(serialised[productType], {
                approval: {
                    duration: this.serialiseDuration(loan_request.term)
                }
            });
        }

        return serialised;
    }

    deserialisePeople(people) {
        return _.map(people, (person) => {
            return this.deserialisePerson(person);
        });
    }

    serialisePeople(people) {
        return _.map(people, (person) => {
            return this.serialisePerson(person);
        });
    }

    deserialisePerson(person) {
        let deserialised = {};

        if (person.emails && person.emails.length > 0) {
            deserialised.email = this.deserialiseEmail(person.emails);
            deserialised.emailUID = this.deserialiseEmailUID(person.emails);
        }

        if (this.hasMarketingEmail(person.emails)) {
            deserialised.marketing_emails = this.deserialiseMarketingEmails(
                person.emails
            );
        }

        if (person.phones) {
            deserialised.phone = this.deserialisePhones(person.phones);
        }

        if (person.residential_addresses) {
            deserialised.addresses = this.deserialiseAddresses(
                person.residential_addresses
            );
        }

        if (person.privacy_policy) {
            deserialised.privacy_policy_agreement_date =
                this.deserialisePrivacyPolicy(person.privacy_policy);
        }

        if (person.title) {
            deserialised.title = this.deserialisePersonTitle(person.title);
        }

        if (person.monthly_non_housing_expenses) {
            // parseInt to get rid of any decimals where the form fields do not support decimals
            deserialised.other_monthly_expenses = parseInt(
                person.monthly_non_housing_expenses.amount
            );
            deserialised.other_monthly_expenses_datetime =
                person.monthly_non_housing_expenses.datetime;
        }

        if (person.monthly_non_business_income) {
            // parseInt to get rid of any decimals where the form fields do not support decimals
            deserialised.monthly_non_business_income = parseInt(
                person.monthly_non_business_income.amount
            );
            deserialised.monthly_non_business_income_datetime =
                person.monthly_non_business_income.datetime;
        }

        if (person.monthly_other_household_income) {
            // parseInt to get rid of any decimals where the form fields do not support decimals
            deserialised.monthly_other_household_income = parseInt(
                person.monthly_other_household_income.amount
            );
            deserialised.monthly_other_household_income_datetime =
                person.monthly_other_household_income.datetime;
        }

        if (person.number_of_dependents) {
            deserialised.number_of_dependents =
                person.number_of_dependents.count;
            deserialised.number_of_dependents_datetime =
                person.number_of_dependents.datetime;
        }

        simpleCheck(deserialised, person, 'roles');
        simpleCheck(deserialised, person, 'uid');
        simpleCheck(deserialised, person, 'first_name');
        simpleCheck(deserialised, person, 'last_name');
        simpleCheck(deserialised, person, 'date_of_birth');
        simpleCheck(deserialised, person, 'how_did_you_find_us');

        return deserialised;
    }

    serialisePerson(person) {
        let serialised = {};

        if (person.email) {
            serialised.emails = this.serialiseEmails(person);
        }

        if (person.phone) {
            serialised.phones = this.serialisePhones(person.phone);
        }

        if (person.addresses) {
            serialised.residential_addresses = this.serialiseAddresses(
                person.addresses
            );
        }

        if (person.privacy_policy_agreement_date) {
            serialised.privacy_policy = this.serialisePrivacyPolicy(person);
        }

        if (person.title) {
            serialised.title = this.serialisePersonTitle(person.title);
        }

        if (_.isNumber(person.other_monthly_expenses)) {
            serialised.monthly_non_housing_expenses = {
                amount: person.other_monthly_expenses,
                datetime: person.other_monthly_expenses_datetime
            };
        }

        if (_.isNumber(person.monthly_non_business_income)) {
            serialised.monthly_non_business_income = {
                amount: person.monthly_non_business_income,
                datetime: person.monthly_non_business_income_datetime
            };
        }

        if (_.isNumber(person.monthly_other_household_income)) {
            serialised.monthly_other_household_income = {
                amount: person.monthly_other_household_income,
                datetime: person.monthly_other_household_income_datetime
            };
        }

        if (_.isNumber(person.number_of_dependents)) {
            serialised.number_of_dependents = {
                count: person.number_of_dependents,
                datetime: person.number_of_dependents_datetime
            };
        }

        simpleCheck(serialised, person, 'roles');
        simpleCheck(serialised, person, 'uid');
        simpleCheck(serialised, person, 'first_name');
        simpleCheck(serialised, person, 'last_name');
        simpleCheck(serialised, person, 'date_of_birth');
        simpleCheck(serialised, person, 'how_did_you_find_us');

        return serialised;
    }

    deserialisePersonTitle(title) {
        const titleTypes = {
            mr: 'Mr',
            ms: 'Ms',
            mrs: 'Mrs',
            miss: 'Miss',
            doctor: 'Dr',
            professor: 'Prof',
            sir: 'Sir',
            lord: 'Lord',
            lady: 'Lady',
            baron: 'Baron',
            baroness: 'Baroness',
            rev: 'Rev',
            other: 'other'
        };

        return titleTypes[_.lowerCase(title)];
    }

    serialisePersonTitle(title) {
        const titleTypes = {
            Mr: 'mr',
            Ms: 'ms',
            Mrs: 'mrs',
            Miss: 'miss',
            Dr: 'doctor',
            Prof: 'professor',
            Sir: 'sir',
            Lord: 'lord',
            Lady: 'lady',
            Baron: 'baron',
            Baroness: 'baroness',
            Rev: 'rev',
            other: 'other'
        };

        return titleTypes[title];
    }

    deserialisePrivacyPolicy(privacy_policy) {
        if (privacy_policy.agreed) {
            return privacy_policy.datetime;
        }
    }

    serialisePrivacyPolicy(person) {
        return {
            agreed: true,
            datetime: person.privacy_policy_agreement_date
        };
    }

    deserialiseAddresses(residential_addresses) {
        return _.map(residential_addresses, (residential_address, index) => {
            const deserialisedAddress =
                this.deserialiseAddress(residential_address);

            const isNotMainAddress = index !== 0;
            if (isNotMainAddress) {
                deserialisedAddress.extra_address = true;
            }

            return deserialisedAddress;
        });
    }

    deserialiseAddress(residential_address) {
        let deserialised = {};

        simpleCheck(deserialised, residential_address, 'uid');
        simpleCheck(deserialised, residential_address, 'town');
        simpleCheck(deserialised, residential_address, 'house_name');
        simpleCheck(deserialised, residential_address, 'house_number');
        simpleCheck(deserialised, residential_address, 'equifax_token');
        simpleCheck(deserialised, residential_address, 'residential_status');
        simpleCheck(deserialised, residential_address, 'street_line_2');
        simpleCheck(deserialised, residential_address, 'street_line_1');
        simpleCheck(deserialised, residential_address, 'postcode');
        simpleCheck(deserialised, residential_address, 'date_from');

        if (residential_address.country) {
            deserialised.country = this.deserialiseCountry(
                residential_address.country
            );
        }

        if (residential_address.monthly_housing_expenses) {
            // parseInt to get rid of any decimals where the form fields do not support decimals
            deserialised.monthly_expenses = parseInt(
                residential_address.monthly_housing_expenses.amount
            );
            deserialised.monthly_expenses_datetime =
                residential_address.monthly_housing_expenses.datetime;
        }

        return deserialised;
    }

    deserialiseCountry(country) {
        //TODO: currently we are only supporting UK
        // eslint-disable-next-line
        switch (country) {
            case 'GB':
                return 'uk';
        }
    }

    serialiseAddresses(addresses) {
        const enteredAddresses = addresses.filter((a) => a);
        return _.map(enteredAddresses, (address) => {
            return this.serialiseAddress(address);
        });
    }

    serialiseAddress(address) {
        let serialised = {};

        simpleCheck(serialised, address, 'uid');
        simpleCheck(serialised, address, 'town');
        simpleCheck(serialised, address, 'house_name');
        simpleCheck(serialised, address, 'house_number');
        simpleCheck(serialised, address, 'equifax_token');
        simpleCheck(serialised, address, 'residential_status');
        simpleCheck(serialised, address, 'street_line_2');
        simpleCheck(serialised, address, 'street_line_1');
        simpleCheck(serialised, address, 'postcode');
        simpleCheck(serialised, address, 'date_from');

        if (address.country) {
            serialised.country = this.serialiseCountry(address.country);
        }

        if (_.isNumber(address.monthly_expenses)) {
            serialised.monthly_housing_expenses = {
                amount: address.monthly_expenses,
                datetime: address.monthly_expenses_datetime
            };
        }

        return serialised;
    }

    serialiseCountry(country) {
        //TODO: currently we are only supporting UK
        //eslint-disable-next-line
        switch (country) {
            case 'uk':
                return 'GB';
        }
    }

    deserialisePhones(phones) {
        return _.map(phones, (phone) => {
            return this.deserialisePhone(phone);
        });
    }

    deserialisePhone(phone) {
        const type = {
            primary: 'main',
            secondary: 'secondary'
        };

        return {
            number: phone.number,
            uid: phone.uid || v4(),
            type: type[phone.type]
        };
    }

    serialisePhones(phones) {
        const enteredPhones = phones.filter((p) => p);
        return _.map(enteredPhones, (phone) => {
            return this.serialisePhone(phone);
        });
    }

    serialisePhone(phone) {
        const type = {
            main: 'primary',
            secondary: 'secondary'
        };

        return {
            number: phone.number,
            uid: phone.uid || v4(),
            type: type[phone.type]
        };
    }

    deserialiseEmail(emails) {
        return emails[0].email;
    }

    serialiseEmails(person) {
        let serialised = {
            email: person.email,
            uid: person.emailUID || v4(),
            type: 'primary'
        };

        if (_.isBoolean(person.marketing_emails)) {
            serialised.marketing_opt_in = { agreed: person.marketing_emails };
        }

        return [serialised];
    }

    deserialiseEmailUID(emails) {
        return emails[0].uid;
    }

    hasMarketingEmail(emails) {
        return (
            _.isArray(emails) &&
            _.isObject(emails[0]) &&
            _.isObject(emails[0].marketing_opt_in)
        );
    }

    deserialiseMarketingEmails(emails) {
        return emails[0].marketing_opt_in.agreed;
    }
}

export { LendingAPIProvider };
